import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
  Stack,
  Alert,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  TableChart as TableChartIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
} from "@mui/icons-material";
import Chart from "react-apexcharts";

interface ChartDetailsData {
  description?: string;
  methodology?: string;
  dataSource?: string;
  tableHeaders: string[];
  tableData: (string | number)[][];
  insights?: string[];
  chartType?: string;
  totalDataPoints?: number;
}

interface ChartCardProps {
  title: string;
  subtitle?: string;
  height?: number;
  children?: React.ReactNode;
  chartOptions?: any;
  chartSeries?: any[];
  chartType?: "line" | "area" | "bar" | "pie" | "donut";
  onMenuClick?: () => void;
  onDetailsClick?: () => void;
  chartDetails?: ChartDetailsData;
  loading?: boolean;
}

// Helper function to generate chart details from chart data
const generateChartDetails = (
  title: string,
  chartOptions: any,
  chartSeries: any[],
  chartType: string
): ChartDetailsData => {
  if (!chartOptions || !chartSeries) {
    return {
      description: "No chart data available to display details.",
      methodology: "Chart configuration is missing or incomplete.",
      dataSource: "No Data Source",
      tableHeaders: ["Status"],
      tableData: [["No data configured"]],
      insights: ["Please configure chart data to view details"],
      chartType: chartType || "unknown",
      totalDataPoints: 0,
    };
  }

  let tableHeaders: string[] = [];
  let tableData: (string | number)[][] = [];
  let insights: string[] = [];
  let totalDataPoints = 0;

  // Generate table based on chart type
  if (chartType === "pie" || chartType === "donut") {
    // For pie/donut charts
    tableHeaders = ["Category", "Value", "Percentage"];
    const labels = chartOptions.labels || [];
    const values = chartSeries || [];
    const total = values.reduce((sum: number, val: number) => sum + val, 0);

    tableData = labels.map((label: string, index: number) => {
      const value = values[index] || 0;
      const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
      return [label, value.toLocaleString(), `${percentage}%`];
    });

    totalDataPoints = labels.length;

    insights = [
      `Total categories analyzed: ${labels.length}`,
      `Largest category represents ${Math.max(
        ...values.map((v: number, i: number) =>
          total > 0 ? (v / total) * 100 : 0
        )
      ).toFixed(1)}% of total`,
      `Total value: ${total.toLocaleString()}`,
    ];
  } else {
    // For line, area, bar charts
    const categories = chartOptions.xaxis?.categories || [];
    const series = chartSeries || [];

    tableHeaders = ["Period", ...series.map((s: any) => s.name || "Series")];

    tableData = categories.map((category: string, index: number) => {
      const row = [category];
      series.forEach((s: any) => {
        const value = s.data?.[index] || 0;
        row.push(typeof value === "number" ? value.toLocaleString() : value);
      });
      return row;
    });

    totalDataPoints = categories.length * series.length;

    // Generate insights
    if (series.length > 0 && series[0].data?.length > 0) {
      const firstSeries = series[0];
      const values = firstSeries.data;
      const average =
        values.reduce((sum: number, val: number) => sum + val, 0) /
        values.length;
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);

      insights = [
        `Average ${firstSeries.name || "value"}: ${average.toLocaleString()}`,
        `Peak value: ${maxValue.toLocaleString()}`,
        `Minimum value: ${minValue.toLocaleString()}`,
        `Data points analyzed: ${values.length}`,
      ];
    }
  }

  // Generate description based on chart type and title
  let description = "";
  let methodology = "";

  switch (chartType) {
    case "area":
      description = `This area chart visualizes ${title.toLowerCase()} trends over time, showing cumulative patterns and growth trajectories.`;
      methodology =
        "Data points are connected with smooth curves and filled areas to emphasize volume and trends.";
      break;
    case "bar":
      description = `This bar chart compares ${title.toLowerCase()} across different categories, enabling direct value comparison.`;
      methodology =
        "Categorical data is represented using rectangular bars with heights proportional to values.";
      break;
    case "line":
      description = `This line chart tracks ${title.toLowerCase()} changes over time, highlighting trends and patterns.`;
      methodology =
        "Time-series data is plotted with connected points to show progression and relationships.";
      break;
    case "pie":
    case "donut":
      description = `This ${chartType} chart shows the composition and distribution of ${title.toLowerCase()}.`;
      methodology =
        "Proportional segments represent relative values as percentages of the whole.";
      break;
    default:
      description = `This chart displays ${title.toLowerCase()} data with interactive visualization capabilities.`;
      methodology =
        "Financial data is processed and visualized using industry-standard charting techniques.";
  }

  return {
    description,
    methodology,
    dataSource: "Enterprise Financial System (EFS)",
    tableHeaders,
    tableData,
    insights,
    chartType,
    totalDataPoints,
  };
};

const ChartDetailsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  chartDetails?: ChartDetailsData;
}> = ({ open, onClose, title, chartDetails }) => {
  const theme = useTheme();

  if (!chartDetails) {
    return null;
  }

  const hasData =
    chartDetails.tableData.length > 0 &&
    chartDetails.tableData[0][0] !== "No data configured" &&
    chartDetails.tableData[0][0] !== "No data available";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "600px",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.secondary.main}04)`,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Detailed analysis and data breakdown
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            bgcolor: "action.hover",
            "&:hover": { bgcolor: "action.selected" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Chart Overview Section */}

        {/* Data Table Section */}
        <Box sx={{ p: 3 }}>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
          >
            <TableChartIcon color="primary" fontSize="small" />
            Chart Data
          </Typography>

          {hasData ? (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2,
                maxHeight: 400,
                "& .MuiTableCell-head": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.04)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                  borderBottom: `2px solid ${theme.palette.divider}`,
                },
                "& .MuiTableCell-body": {
                  fontSize: "0.875rem",
                  "&:first-of-type": {
                    fontWeight: 600,
                  },
                },
                "& .MuiTableRow-root": {
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                    transition: "background-color 0.2s ease",
                  },
                  "&:nth-of-type(even)": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.02)"
                        : "rgba(0, 0, 0, 0.02)",
                  },
                },
              }}
            >
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow>
                    {chartDetails.tableHeaders.map((header, index) => (
                      <TableCell key={index}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartDetails.tableData.map((row, index) => (
                    <TableRow key={index}>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>
                          {typeof cell === "number"
                            ? cell.toLocaleString()
                            : cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                "& .MuiAlert-message": {
                  fontSize: "0.875rem",
                },
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                No Data Available
              </Typography>
              <Typography variant="body2">
                This chart doesn't have detailed data configured. The
                visualization may be using aggregated or computed values.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  height = 400,
  children,
  chartOptions,
  chartSeries,
  chartType = "line",
  onMenuClick,
  onDetailsClick,
  chartDetails,
  loading = false,
}) => {
  const theme = useTheme();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleDetailsClick = () => {
    setDetailsOpen(true);
    if (onDetailsClick) {
      onDetailsClick();
    }
  };

  // Generate chart details from actual chart data if not provided
  const finalChartDetails =
    chartDetails ||
    generateChartDetails(title, chartOptions, chartSeries || [], chartType);

  return (
    <>
      <Box
        sx={{
          height: height,
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "primary.main",
            boxShadow: `0 4px 20px ${theme.palette.primary.main}20`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            pb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                mb: subtitle ? 0.5 : 0,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Button
              size="small"
              onClick={handleDetailsClick}
              variant="outlined"
              startIcon={<InfoIcon />}
              sx={{
                textTransform: "none",
                borderRadius: 1.5,
                fontSize: "0.75rem",
                fontWeight: 600,
                px: 2,
                py: 0.75,
                minWidth: "auto",
                borderColor: theme.palette.divider,
                color: "text.secondary",
                "&:hover": {
                  borderColor: "primary.main",
                  color: "primary.main",
                  bgcolor: "primary.50",
                },
              }}
            >
              Details
            </Button>
            {onMenuClick && (
              <IconButton
                size="small"
                onClick={onMenuClick}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            pt: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {loading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              Loading...
            </Box>
          ) : children ? (
            children
          ) : chartOptions && chartSeries ? (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type={chartType}
              height="100%"
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              No data available
            </Box>
          )}
        </Box>
      </Box>

      {/* Details Dialog */}
      <ChartDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={title}
        chartDetails={finalChartDetails}
      />
    </>
  );
};
